import React, { useState } from "react";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
  useRowSelect,
} from "react-table";
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Button,
} from "@mui/material";
import Empty from "../error/Empty";
import TableFooter from "./sub-components/TableFooter";
import TableHeader from "./sub-components/TableHeader";
import TableFeature from "./sub-components/TableFeature";
import TableCustomizerDialog from "./sub-components/TableCustomizerDialog";
import { exportData, getExportableData } from "../../manager/dataManager";
import TableLoader from "../TableLoader";
import { grnDefaultColumn } from "./cell/grnTableCustomCell";
import QCDetailModal from "../QCDetailModal";
import TableFilterPopOver from "./sub-components/TableFilterPopOver";
import TableHeaderLayout from "./sub-components/TableHeaderLayout";
import GRNViewModal from "../GRNViewModal";
import TableCheckbox from "./sub-components/TableCheckbox";
import { Download } from "@mui/icons-material";

function GRNTable({
  columns,
  data,
  fromDate,
  toDate,
  handleFromDate,
  handleToDate,
  callAPI,
  PONumber,
  setPONumber,
  RCPTNumber,
  setRCPTNumber,
  loading,
  detailsAPI,
  detailsColumn,
  moduleName,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showTableCustomizer, setShowTableCustomizer] = useState(false);
  const [showQCDetails, setShowQCDetails] = useState(null);
  const [grnAction, setGRNAction] = useState(null);

  const handleGRNModalOpen = (data) => {
    setGRNAction(data);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    pageOptions,
    state,
    setPageSize,
    canPreviousPage,
    setGlobalFilter,
    prepareRow,
    allColumns,
    getToggleHideAllColumnsProps,
    selectedFlatRows,
    gotoPage,
    pageCount,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: grnDefaultColumn,
      initialState: {
        pageSize: 12,
        hiddenColumns: columns
          .filter((col) => col.show === false)
          .map((col) => col.accessor),
      },
      handleGRNModalOpen,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => {
        return [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <TableCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
              <TableCheckbox {...row.getToggleRowSelectedProps()} />
            ),
          },
          ...columns,
        ];
      });
    }
  );

  const { pageIndex, pageSize, globalFilter } = state;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TableCustomizerDialog
        open={showTableCustomizer}
        handleClose={() => setShowTableCustomizer(false)}
        allColumns={allColumns}
        getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
      />
      <QCDetailModal
        tableQCData={showQCDetails}
        handleClose={() => setShowQCDetails(null)}
        detailsAPI={detailsAPI}
        columns={detailsColumn}
      />
      <GRNViewModal
        purchaseOrder={grnAction}
        handleClose={() => setGRNAction(null)}
      />
      <TableFilterPopOver
        open={open}
        handleClose={handleClose}
        anchorEl={anchorEl}
        fromDate={fromDate}
        toDate={toDate}
        callAPI={callAPI}
        setPONumber={setPONumber}
        PONumber={PONumber}
        RCPTNumber={RCPTNumber}
        setRCPTNumber={setRCPTNumber}
        handleFromDate={handleFromDate}
        handleToDate={handleToDate}
      />
      <TableHeaderLayout
        setGlobalFilter={setGlobalFilter}
        globalFilter={globalFilter}
        fromDate={fromDate}
        toDate={toDate}
      >
        {selectedFlatRows.length === 0 ? (
          <TableFeature
            handleFilter={handleClick}
            openCustomizer={() => setShowTableCustomizer(true)}
            refresh={callAPI}
            exportHandler={() =>
              exportData(
                getExportableData(data, columns),
                `GRN ${fromDate} to ${toDate}`
              )
            }
            fromDate={fromDate}
            toDate={toDate}
          />
        ) : (
          <Button
            variant="outlined"
            endIcon={<Download sx={{ fontSize: "10px" }} />}
            size="small"
            onClick={() => {
              exportData(
                getExportableData(
                  selectedFlatRows.map((row) => row.original),
                  columns
                ),
                `GRN ${fromDate} to ${toDate}`
              );
            }}
          >
            Export Selected
          </Button>
        )}
      </TableHeaderLayout>
      {data.length === 0 && loading === false && (
        <Empty title={"No data found"} />
      )}
      {data.length !== 0 && loading === false && (
        <>
          <TableContainer component={Paper}>
            <Table
              {...getTableProps()}
              sx={{
                borderCollapse: "separate",
                minWidth: "max-content",
              }}
            >
              <TableHeader headerGroups={headerGroups} />
              <TableBody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <TableRow
                      {...row.getRowProps()}
                      sx={{
                        "& td": {
                          bgcolor: "background.paper",
                          cursor: "pointer",
                        },
                      }}
                      onDoubleClick={() => {
                        handleGRNModalOpen(row.original);
                      }}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <TableCell {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TableFooter
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            pageOptions={pageOptions}
            pageIndex={pageIndex}
            nextPage={nextPage}
            previousPage={previousPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
            gotoPage={gotoPage}
            pageCount={pageCount}
          />
        </>
      )}
      {loading === true && <TableLoader title={"Loading..."} />}
    </>
  );
}

export default GRNTable;
